import { apiClient } from '@/lib/api/client'

export interface TwoFactorSettings {
  enabled: boolean
  required: boolean
  backup_codes_count: number
}

export interface SecuritySettings {
  session_timeout: number
  max_login_attempts: number
  password_min_length: number
  password_require_special_chars: boolean
  password_require_numbers: boolean
}

export interface SecuritySettingsData {
  two_factor: {
    enabled: boolean
    required: boolean
    backup_codes_count: number
  }
  session: {
    session_enabled: boolean
    session_timeout: number
    max_login_attempts: number
    lockout_duration: number
  }
}

export interface GeneralSettingsData {
  site: {
    site_name: string
    site_description: string
    auth_logo?: string | File | null
    sidenav_logo?: string | File | null
  }
  date_time: {
    timezone: string
    date_format: string
    time_format: string
  }
  language: {
    default_language: string
  }
}

export interface EmailSettings {
  mailer: string
  mail_from_name: string
  mail_from_address: string
  smtp?: {
    host: string
    port: string
    encryption: string
    username: string
    password: string
  }
  mailgun?: {
    domain: string
    secret: string
  }
  postmark?: {
    token: string
  }
  ses?: {
    key: string
    secret: string
    region: string
  }
  microsoft?: {
    tenant_id: string
    client_id: string
    client_secret: string
    sender_email: string
  }
}

export interface EmailSettingsData {
  mailer: string
  mail_from_name: string
  mail_from_address: string
  smtp: {
    host: string
    port: string
    encryption: string
    username: string
    password: string
  }
  mailgun: {
    domain: string
    secret: string
  }
  postmark: {
    token: string
  }
  ses: {
    key: string
    secret: string
    region: string
  }
  microsoft: {
    tenant_id: string
    client_id: string
    client_secret: string
    sender_email: string
  }
}

export interface UISettings {
  items_per_page: number
  theme_color: string
  sidebar_collapsed: boolean
}

export interface GeneralSettings {
  site: {
    site_name: string
    site_description: string
    auth_logo?: string | File | null
    sidenav_logo?: string | File | null
  }
  date_time: {
    timezone: string
    date_format: string
    time_format: string
  }
  language: {
    default_language: string
  }
}

export interface SystemSettings {
  two_factor: TwoFactorSettings
  security: SecuritySettings
  general: GeneralSettingsData
  email: EmailSettings
  ui: UISettings
}

export interface TwoFactorStatus {
  isEnabled: boolean
  isRequired: boolean
  hasBackupCodes: boolean
  lastUsedAt?: string
}

export const SettingsService = {
  async getSettings(): Promise<{ success: boolean; data: SystemSettings }> {
    try {
      const response = await apiClient.get('/system-settings/settings')
      return response.data
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch settings')
    }
  },

  async updateSettings(settings: Partial<SystemSettings>): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiClient.post('/system-settings/settings', settings)
      return response.data
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to update settings')
    }
  },

  async getOption(key: string): Promise<{ success: boolean; data: { key: string; value: any } }> {
    try {
      const response = await apiClient.get(`/system-settings/settings/${key}`)
      return response.data
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch option')
    }
  },

  async updateOption(key: string, value: any, type: string = 'string', description?: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiClient.put(`/system-settings/settings/${key}`, {
        value,
        type,
        description
      })
      return response.data
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to update option')
    }
  },

  async getTwoFactorStatus(): Promise<{ success: boolean; data: TwoFactorStatus }> {
    try {
      const response = await apiClient.get('/system-settings/settings/two-factor/status')
      return response.data
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch 2FA status')
    }
  },

  async enableTwoFactor(): Promise<{ success: boolean; message: string; data: any }> {
    try {
      const response = await apiClient.post('/system-settings/settings/two-factor/enable')
      return response.data
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to enable 2FA')
    }
  },

  async disableTwoFactor(): Promise<{ success: boolean; message: string; data: any }> {
    try {
      const response = await apiClient.post('/system-settings/settings/two-factor/disable')
      return response.data
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to disable 2FA')
    }
  },

  async generateBackupCodes(): Promise<{ success: boolean; message: string; data: { backupCodes: string[] } }> {
    try {
      const response = await apiClient.post('/system-settings/settings/two-factor/backup-codes')
      return response.data
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to generate backup codes')
    }
  },

  async initializeDefaults(): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiClient.post('/system-settings/settings/initialize')
      return response.data
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to initialize defaults')
    }
  },

  async getGeneralSettings(): Promise<{ success: boolean; data: GeneralSettingsData }> {
    try {
      const response = await apiClient.get('/system-settings/settings/general')
      return response.data
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch general settings')
    }
  },

  async updateGeneralSettings(settings: Partial<GeneralSettingsData>): Promise<{ success: boolean; message: string }> {
    try {
      const hasFiles = settings.site && (
        settings.site.auth_logo instanceof File ||
        settings.site.sidenav_logo instanceof File
      )

      if (hasFiles) {
        const formData = new FormData()
        
        if (settings.site) {
          if (settings.site.auth_logo instanceof File) {
            formData.append('site[auth_logo]', settings.site.auth_logo)
          } else if (settings.site.auth_logo === null) {
            formData.append('site[auth_logo]', '')
            formData.append('site[remove_auth_logo]', '1')
          }
          
          if (settings.site.sidenav_logo instanceof File) {
            formData.append('site[sidenav_logo]', settings.site.sidenav_logo)
          } else if (settings.site.sidenav_logo === null) {
            formData.append('site[sidenav_logo]', '')
            formData.append('site[remove_sidenav_logo]', '1')
          }
          
          if (settings.site.site_name !== undefined) {
            formData.append('site[site_name]', settings.site.site_name)
          }
          if (settings.site.site_description !== undefined) {
            formData.append('site[site_description]', settings.site.site_description)
          }
        }
        
        if (settings.date_time) {
          Object.entries(settings.date_time).forEach(([key, value]) => {
            formData.append(`date_time[${key}]`, value as string)
          })
        }
        
        if (settings.language) {
          Object.entries(settings.language).forEach(([key, value]) => {
            formData.append(`language[${key}]`, value as string)
          })
        }
        
        const response = await apiClient.post('/system-settings/settings/general', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
        return response.data
      } else {
        const settingsData: any = { ...settings }
        if (settingsData.site) {
          if (settingsData.site.auth_logo !== null && !(settingsData.site.auth_logo instanceof File)) {
            delete settingsData.site.auth_logo
          }
          if (settingsData.site.sidenav_logo !== null && !(settingsData.site.sidenav_logo instanceof File)) {
            delete settingsData.site.sidenav_logo
          }
        }
        const response = await apiClient.post('/system-settings/settings/general', settingsData)
        return response.data
      }
    } catch (error: any) {
      if (error.response?.data) {
        const errorData = error.response.data
        const customError: any = new Error(errorData.message || 'Failed to update general settings')
        customError.response = { data: errorData }
        throw customError
      }
      throw new Error(error.message || 'Failed to update general settings')
    }
  },

  async getSecuritySettings(): Promise<{ success: boolean; data: SecuritySettingsData }> {
    try {
      const response = await apiClient.get('/system-settings/settings/security')
      return response.data
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch security settings')
    }
  },

  async updateSecuritySettings(settings: Partial<SecuritySettingsData>): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiClient.post('/system-settings/settings/security', settings)
      return response.data
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to update security settings')
    }
  },

  async getEmailSettings(): Promise<{ success: boolean; data: EmailSettingsData }> {
    try {
      const response = await apiClient.get('/system-settings/settings/email')
      return response.data
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch email settings')
    }
  },

  async updateEmailSettings(settings: Partial<EmailSettingsData>): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiClient.post('/system-settings/settings/email', settings)
      return response.data
    } catch (error: any) {
      if (error.response?.data) {
        const errorData = error.response.data
        const customError: any = new Error(errorData.message || 'Failed to update email settings')
        customError.response = { data: errorData }
        throw customError
      }
      throw new Error(error.message || 'Failed to update email settings')
    }
  }
}

